import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { DailyReportData } from '@/services/types';
import { Avatar, Card } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
/** The backend reports `hour` in UTC; shift to the browser's local hour before display. */
const localHour = (h: number) => (h - new Date().getTimezoneOffset() / 60 + 24) % 24;

/** "Penjualan per jam" bar chart — shared by the daily report and the dashboard (every range). */
export const HourlyCard: React.FC<{ data: Pick<DailyReportData, 'salesByHour'>; className?: string; children?: React.ReactNode }> = ({ data, className, children }) => {
  const { t } = useTranslation();
  const rows = data.salesByHour.map((h) => ({ ...h, hour: localHour(h.hour) })).sort((a, b) => a.hour - b.hour);
  const peakHour = rows.reduce((max, h) => (h.sales > max.sales ? h : max), { hour: 0, sales: 0, transactions: 0 });
  const maxHourSales = peakHour.sales || 0;
  return (
    <Card padding="sm" className={clsx('flex flex-col gap-3 lg:p-[22px_24px]', className)}>
      <div className="flex items-baseline justify-between"><span className="text-[15px] font-extrabold lg:text-base">{t('report.salesByHour')}</span>{peakHour.sales > 0 && <span className="text-xs font-bold text-ink-muted">{t('report.peakHour', { hour: `${hh(peakHour.hour)}–${hh((peakHour.hour + 1) % 24)}` })}</span>}</div>
      {rows.length === 0 ? <p className="text-sm text-ink-muted">{t('dashboard.noData')}</p> : (
        <div className="flex h-36 items-end gap-1.5 lg:h-44 lg:gap-2.5">
          {rows.map((h) => {
            const pct = maxHourSales > 0 ? (h.sales / maxHourSales) * 100 : 0;
            const top = h.hour === peakHour.hour && h.sales > 0;
            return (
              <div key={h.hour} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5" title={`${hh(h.hour)} · ${formatCurrency(h.sales)} · ${h.transactions}`}>
                <span className={clsx('w-full rounded-[10px_10px_4px_4px]', top ? 'bg-accent-500' : pct > 40 ? 'bg-primary-500' : pct > 0 ? 'bg-primary-100' : 'bg-neutral-200')} style={{ height: pct > 0 ? `${Math.max(pct, 4)}%` : 4 }} />
                <span className="text-[10px] text-ink-muted tnum lg:text-[11px]">{String(h.hour).padStart(2, '0')}</span>
              </div>
            );
          })}
        </div>
      )}
      {children && <div className="flex flex-col gap-2 border-t border-line-subtle pt-3">{children}</div>}
    </Card>
  );
};

/** Per-cashier tiles, sorted by sales. `compact` = dashboard "Staf bertugas" list (top 5, "52 trx · Rp 2,9 jt" on one line). */
export const CashierCard: React.FC<{ data: Pick<DailyReportData, 'salesByCashier'>; title?: string; compact?: boolean; className?: string }> = ({ data, title, compact, className }) => {
  const { t } = useTranslation();
  const sorted = [...data.salesByCashier].sort((a, b) => b.totalSales - a.totalSales);
  const rows = compact ? sorted.slice(0, 5) : sorted;
  return (
    <Card padding="sm" className={clsx('flex flex-col gap-3 lg:p-[18px_22px]', className)}>
      <span className="text-[15px] font-extrabold lg:text-base">{title ?? t('report.salesByCashier')}</span>
      {rows.length === 0 ? <p className="text-sm text-ink-muted">{t('dashboard.noData')}</p> : (
        <div className={clsx('flex flex-col gap-1', !compact && 'lg:grid lg:grid-cols-3 lg:gap-3')}>
          {rows.map((c) => (
            <div key={c.cashierId} className={clsx('flex items-center gap-2.5 py-2', !compact && 'lg:rounded-md lg:bg-surface-sunken lg:p-3')}>
              <Avatar name={c.cashierName} size="sm" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-extrabold">{c.cashierName}</span>
                {compact
                  ? <span className="text-xs text-ink-muted tnum">{t('dashboard.staffStats', { count: c.transactionCount, amount: formatCurrency(c.totalSales) })}</span>
                  : <span className="text-xs text-ink-muted">{t('report.transactionsCount', { count: c.transactionCount })}</span>}
              </span>
              {!compact && <span className="text-sm font-black tnum">{formatCurrency(c.totalSales)}</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
